import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownSelect } from '../common/DropdownSelect';
import { InputGroup } from '../common/InputGroup';

const ACTION_TYPES = [
  'purchase',
  'refund',
  'admin_adjustment',
  'signup_gift',
  'monthly_gift',
  'send_alliance_request',
  'accept_quote_request',
  'accept_quote_alliance_request',
  'submit_public_job_quote',
  'publish_job_standard',
  'publish_job_featured',
  'publish_job_urgent',
] as const;

type PeriodPreset = 'all' | 'last_week' | 'last_month' | 'last_quarter' | 'custom';

interface FilterOption {
  value: string;
  label: string;
}

interface CreditTransactionFilterBarProps {
  filters: {
    search: string;
    actionType: string;
    startDate: string;
    endDate: string;
  };
  onFilterChange: (name: keyof CreditTransactionFilterBarProps['filters'], value: string) => void;
}

// Format using the browser's local calendar date (not UTC) so presets don't
// shift by a day for users in negative-offset timezones.
const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const computePresetRange = (preset: PeriodPreset): { startDate: string; endDate: string } => {
  const now = new Date();
  const endDate = toDateInputValue(now);

  switch (preset) {
    case 'last_week': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      return { startDate: toDateInputValue(start), endDate };
    }
    case 'last_month': {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      return { startDate: toDateInputValue(start), endDate };
    }
    case 'last_quarter': {
      const start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      return { startDate: toDateInputValue(start), endDate };
    }
    default:
      return { startDate: '', endDate: '' };
  }
};

const CreditTransactionFilterBar: React.FC<CreditTransactionFilterBarProps> = ({
  filters,
  onFilterChange,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  const [periodPreset, setPeriodPreset] = React.useState<PeriodPreset>('all');

  const actionTypeOptions: FilterOption[] = [
    { value: '', label: t('all_types', { ns: 'common', defaultValue: 'Toutes les types' }) },
    ...ACTION_TYPES.map(actionType => ({
      value: actionType,
      label: t(`creditTransactions.actionTypes.${actionType}`, { ns: 'admin' }),
    })),
  ];

  const periodOptions: FilterOption[] = [
    { value: 'all', label: t('creditTransactions.periodPresets.all', { ns: 'admin' }) },
    { value: 'last_week', label: t('creditTransactions.periodPresets.lastWeek', { ns: 'admin' }) },
    {
      value: 'last_month',
      label: t('creditTransactions.periodPresets.lastMonth', { ns: 'admin' }),
    },
    {
      value: 'last_quarter',
      label: t('creditTransactions.periodPresets.lastQuarter', { ns: 'admin' }),
    },
    { value: 'custom', label: t('creditTransactions.periodPresets.custom', { ns: 'admin' }) },
  ];

  const handlePresetChange = (preset: PeriodPreset) => {
    setPeriodPreset(preset);

    if (preset === 'custom') {
      return;
    }

    const { startDate, endDate } = computePresetRange(preset);
    onFilterChange('startDate', startDate);
    onFilterChange('endDate', endDate);
  };

  const handleDateChange = (name: 'startDate' | 'endDate', value: string) => {
    // Manually editing a date implies a custom range.
    setPeriodPreset('custom');
    onFilterChange(name, value);
  };

  return (
    <div className="flex flex-wrap gap-3 items-end justify-end px-4 py-3 border-b border-gray-300 bg-gray-50">
      <div className="w-56">
        <InputGroup
          value={filters.search}
          onChange={e => onFilterChange('search', e.target.value)}
          placeholder={t('creditTransactions.searchPlaceholder', {
            ns: 'admin',
            defaultValue: 'Nom ou courriel',
          })}
        />
      </div>

      <DropdownSelect<FilterOption>
        className="w-56"
        value={actionTypeOptions.find(option => option.value === filters.actionType) ?? null}
        onChange={(_label, item) => onFilterChange('actionType', item?.value ?? '')}
        options={actionTypeOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value || 'all'}
        placeholder={t('all_types', { ns: 'common', defaultValue: 'Toutes les types' })}
      />

      <DropdownSelect<FilterOption>
        className="w-48"
        value={periodOptions.find(option => option.value === periodPreset) ?? null}
        onChange={(_label, item) => handlePresetChange((item?.value ?? 'all') as PeriodPreset)}
        options={periodOptions}
        getOptionLabel={option => option.label}
        getOptionValue={option => option.value}
        placeholder={t('creditTransactions.period', { ns: 'admin' })}
      />

      <div className="w-40">
        <InputGroup
          type="date"
          value={filters.startDate}
          onChange={e => handleDateChange('startDate', e.target.value)}
          aria-label={t('creditTransactions.startDate', { ns: 'admin' })}
        />
      </div>

      <div className="w-40">
        <InputGroup
          type="date"
          value={filters.endDate}
          onChange={e => handleDateChange('endDate', e.target.value)}
          aria-label={t('creditTransactions.endDate', { ns: 'admin' })}
        />
      </div>
    </div>
  );
};

export default CreditTransactionFilterBar;
